import React from "react";
import useSpeechToText from "react-hook-speech-to-text";
import { ClickAwayListener, IconButton } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import AppDrawer from "../UtilsComponents/AppDrawer";
import { useSelector } from "react-redux";
import { deviceSizeStore } from "../../../Store/Slices/socket/DeviceSizeSlice";
import AppSmallButton from "../UtilsComponents/AppSmallButton";
import { AppMaterialIcons } from "../Icons";
import { AppVoiceButtonWrapper } from "../../Styles/StyledComponents";
import ActiveMic from "../../../assets/images/activeMicRecording.gif";
import voiceLoader from "../../../assets/images/voiceLoader.gif";

let recordingStart: any = new Audio(
  require("../../../assets/recording-start.mp3")
);

let recordingStop: any = new Audio(
  require("../../../assets/recording-end.mp3")
);

interface Props {
  onVoiceInput?: (text: string) => void;
}

const AppVoiceButton: React.FC<Props> = ({ onVoiceInput }) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const IframeDimensions = useSelector(deviceSizeStore);
  const [inputText, setInputText] = React.useState("");

  const {
    error,
    interimResult,
    isRecording,
    results,
    startSpeechToText,
    stopSpeechToText,
  } = useSpeechToText({
    continuous: false,
    useLegacyResults: false,
    crossBrowser: true,
    timeout: 5000,
    interimResults: true,
    googleApiKey: "ed02d1ea7a7b19b16d6d485441f52ca689397d18",
    googleCloudRecognitionConfig: {
      languageCode: "en-US",
    },
  });

  React.useEffect(() => {
    if (error) {
      console.error('Speech Recognition Error:', error);
      
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        console.error('Web Speech API requires HTTPS or localhost');
        alert('Voice features require a secure connection (HTTPS) or localhost. Please ensure you are using HTTPS or running on localhost.');
      }
      
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        console.error('Speech Recognition API not supported in this browser');
        alert('Your browser does not support speech recognition. Please try using the latest version of Chrome.');
      }
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error('getUserMedia is not supported');
        alert('Microphone access is not supported in your browser. Please check your browser settings and ensure microphone permissions are granted.');
      }
    }
  }, [error]);

  React.useEffect(() => {
    setTimeout(() => {
      if (!isRecording && results.length > 0) {
        setOpen(false);
        const lastResult = results[results.length - 1];
        let voiceText = "";
        
        if (typeof lastResult === "string") {
          voiceText = lastResult;
        } else if (typeof lastResult === "object" && "transcript" in lastResult) {
          voiceText = lastResult.transcript;
        }
        
        setInputText(voiceText);
        if (onVoiceInput) {
          onVoiceInput(voiceText);
        }
        results.splice(0, results.length);
      }
    }, 3000);
  }, [isRecording, results, onVoiceInput]);

  const startRrcording = () => {
    setOpen(true);
    startSpeechToText();
    recordingStart.play();
  };

  const stopRecording = () => {
    setOpen(false);
    stopSpeechToText();
  };

  if (error) return <p>Web Speech API is not available in this browser 🤷‍</p>;

  return (
    <AppVoiceButtonWrapper>
      <IconButton
        onClick={() => {
          startRrcording();
        }}
        className="inputActionBox"
      >
        <MicIcon />
      </IconButton>
      <AppDrawer
        anchor={"bottom"}
        showDrawer={open}
        setShowDrawer={setOpen}
        style={{
          bottom: "0px",
          left: "0px",
          maxWidth: "100%",
          borderRadius: "0px",
          overflowX: "hidden",
          paddingBottom: 0,
          maxHeight: "40vh",
          padding: "6px",
          boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.1)",
          display: "flex",
          justifyContent: "center",
        }}
        intialX={0}
        intialY={"5%"}
        animateX={0}
        animateY={0}
        exitX={0}
        exitY={"+100%"}
        animateHeight="40vh"
      >
        <ClickAwayListener
          onClickAway={() => {
            setOpen(false);
            stopSpeechToText();
          }}
        >
          <div className="speech-area">
            <div className="header">
              <IconButton
                onClick={() => {
                  setOpen(false);
                  stopSpeechToText();
                }}
              >
                <AppMaterialIcons iconName="Close" />
              </IconButton>
            </div>

            <div className="speech-body">
              {isRecording ? (
                <>
                  <img src={ActiveMic} width="115px" />
                  <p>Listening...</p>
                </>
              ) : (
                <>
                  {results?.length ? (
                    <img src={voiceLoader} width="115px" />
                  ) : (
                    <>
                      <IconButton
                        style={{ marginBottom: "1.6rem" }}
                        onClick={() => startSpeechToText()}
                      >
                        <AppMaterialIcons iconName="MicNone" />
                      </IconButton>
                      <AppSmallButton
                        variant="text"
                        onClick={() => startSpeechToText()}
                      >
                        Click To Record
                      </AppSmallButton>
                    </>
                  )}
                </>
              )}
              <div>
                {results.length > 0 ? (
                  <p style={{ color: "#000" }}>
                    {(results[results.length - 1] as any).transcript ||
                      (results[results.length - 1] as string)}
                  </p>
                ) : null}

                {interimResult && results.length > 0 && (
                  <p style={{ color: "blue" }}>{interimResult}</p>
                )}
              </div>
            </div>

            <div className="footer"></div>
          </div>
        </ClickAwayListener>
      </AppDrawer>
    </AppVoiceButtonWrapper>
  );
};

export default AppVoiceButton;
